import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "agent-reply-draft",
  "aria-label": "AI suggested reply"
}
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "agent-reply-draft__bar-preview" }
const _hoisted_4 = { class: "agent-reply-draft__bar-action" }
const _hoisted_5 = {
  key: 1,
  class: "agent-reply-draft__expanded"
}
const _hoisted_6 = ["onClick"]

import { computed, onBeforeUnmount, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { Socket } from "socket.io-client";
import { Button, Icon } from "@/components/ui";
import AgentActionCard from "@/components/agent/AgentActionCard.vue";
import AgentService from "@/services/ai/agent";
import { useAgentActions } from "@/composables/agent/useAgentActions";
import { AGENT_UNREAD_REPLY_DRAFTS_KEY } from "@/composables/agent/useReplyDraftInbox";
import { getSocket } from "@/utils/socket";
import type { AgentAction, AgentReplyDraftEvent } from "@/dtos/Agent.dto";

// The AI's suggested answer to a contact's latest email or LinkedIn message, shown in the
// conversation. The rep sends it as is, edits it, takes it into the reply box to rewrite,
// or dismisses it. Drafts are written in the background when a message arrives
// (reply-drafts.ts on the server) and announced over the socket.

const REPLY_DRAFT_EVENT = "agent:reply-draft";
const DISMISSED = "Dismissed";
const MOVED_TO_REPLY_BOX = "Moved to the reply box";


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentReplyDraft',
  props: {
    contactId: {},
    channel: {},
    emailIds: {},
    showOpen: { type: Boolean }
  },
  emits: ["use", "open"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const queryClient = useQueryClient();
const queryKey = computed(() => ["agent-reply-drafts", props.contactId]);

const { data } = useQuery({
  queryKey,
  queryFn: async (): Promise<AgentAction[]> => {
    const result = await AgentService.listActions({
      status: "pending",
      contactId: props.contactId!,
      source: "reply",
      limit: 10,
    });
    return result.data?.data ?? [];
  },
  enabled: computed(() => !!props.contactId),
});

const replyToEmailIdOf = (action: AgentAction): string | undefined =>
  (action.editedPayload ?? action.payload)?.replyToEmailId;

const drafts = computed(() =>
  (data.value ?? []).filter((action) => {
    if (props.channel === "linkedin") return action.actionType === "send_linkedin_message";
    if (action.actionType !== "send_email") return false;
    if (!props.emailIds) return true;
    const replyTo = replyToEmailIdOf(action);
    return !!replyTo && props.emailIds.includes(replyTo);
  })
);

// Drafts open collapsed so the conversation stays in view; the rep expands one to review it.
const expandedIds = ref(new Set<string>());
const toggle = (id: string) => {
  const next = new Set(expandedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedIds.value = next;
};

/** The draft's text on one line, for the collapsed bar (email bodies are HTML). */
const previewOf = (action: AgentAction): string => {
  const input = action.editedPayload ?? action.payload ?? {};
  const raw: string = (action.actionType === "send_email" ? input.body : input.text) ?? "";
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// Keeps the card's status in step with each decision: "Sent", "Failed"…
const updateAction = (action: AgentAction) => {
  queryClient.setQueryData<AgentAction[]>(queryKey.value, (list) =>
    list?.map((a) => (a.id === action.id ? { ...action, contact: action.contact ?? a.contact } : a))
  );
  // Handled here, so the assistant bubble stops announcing it.
  if (action.status !== "pending") {
    queryClient.invalidateQueries({ queryKey: AGENT_UNREAD_REPLY_DRAFTS_KEY });
  }
};
const { busyIds, approve, reject } = useAgentActions(updateAction);

/** Rejects the draft and takes its card away; nothing more to show for it. */
const dismiss = async (action: AgentAction, reason: string) => {
  if (!(await reject(action, reason))) return;
  queryClient.setQueryData<AgentAction[]>(queryKey.value, (list) =>
    list?.filter((a) => a.id !== action.id)
  );
};

const useInReplyBox = (action: AgentAction) => {
  const input = action.editedPayload ?? action.payload;
  emit("use", {
    text: action.actionType === "send_email" ? input.body : input.text,
    subject: input.subject,
    replyToEmailId: input.replyToEmailId,
  });
  // The rep owns the text now; the draft itself is done with.
  return dismiss(action, MOVED_TO_REPLY_BOX);
};

// Ticks so a draft left open shows when it expires.
const now = ref(Date.now());
const timer = window.setInterval(() => (now.value = Date.now()), 30_000);

const onDraft = (event: AgentReplyDraftEvent) => {
  if (event?.contactId === props.contactId) {
    queryClient.invalidateQueries({ queryKey: queryKey.value });
  }
};

// Live updates are a nicety: drafts still load with the conversation without the socket.
let socket: Socket | null = null;
try {
  socket = getSocket();
  socket.on(REPLY_DRAFT_EVENT, onDraft);
} catch (err) {
  console.error("AI reply draft updates are unavailable:", err);
}

onBeforeUnmount(() => {
  window.clearInterval(timer);
  socket?.off(REPLY_DRAFT_EVENT, onDraft);
});

return (_ctx: any,_cache: any) => {
  return (drafts.value.length)
    ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(drafts.value, (action) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: action.id
          }, [
            (!expandedIds.value.has(action.id))
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "agent-reply-draft__bar",
                  "aria-expanded": false,
                  onClick: ($event: any) => (toggle(action.id))
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:creation",
                    class: "agent-reply-draft__bar-icon"
                  }),
                  _cache[1] || (_cache[1] = _createElementVNode("span", { class: "agent-reply-draft__bar-title" }, "AI suggested reply", -1)),
                  _createElementVNode("span", _hoisted_3, _toDisplayString(previewOf(action)), 1),
                  _createElementVNode("span", _hoisted_4, [
                    _cache[0] || (_cache[0] = _createTextVNode(" Review ", -1)),
                    _createVNode(_unref(Icon), { icon: "mdi:chevron-up" })
                  ])
                ], 8, _hoisted_2))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "agent-reply-draft__hide",
                    "aria-expanded": true,
                    onClick: ($event: any) => (toggle(action.id))
                  }, [
                    _cache[2] || (_cache[2] = _createTextVNode(" Hide suggestion ", -1)),
                    _createVNode(_unref(Icon), { icon: "mdi:chevron-down" })
                  ], 8, _hoisted_6),
                  _createVNode(AgentActionCard, {
                    action: action,
                    busy: _unref(busyIds).has(action.id),
                    now: now.value,
                    title: "AI suggested reply",
                    "approve-label": "Send",
                    "reject-label": "Dismiss",
                    "quick-reject": "",
                    onApprove: (payload) => _unref(approve)(action, payload),
                    onReject: (reason) => dismiss(action, reason ?? DISMISSED)
                  }, {
                    actions: _withCtx(({ busy }) => [
                      (__props.showOpen && replyToEmailIdOf(action))
                        ? (_openBlock(), _createBlock(_unref(Button), {
                            key: 0,
                            variant: "outline",
                            size: "sm",
                            disabled: busy,
                            onClick: ($event: any) => (emit('open', replyToEmailIdOf(action)!))
                          }, {
                            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                              _createTextVNode(" Open email ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled", "onClick"]))
                        : (!__props.showOpen)
                          ? (_openBlock(), _createBlock(_unref(Button), {
                              key: 1,
                              variant: "outline",
                              size: "sm",
                              disabled: busy,
                              onClick: ($event: any) => (useInReplyBox(action))
                            }, {
                              default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                _createTextVNode(" Use in reply box ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["disabled", "onClick"]))
                          : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1032, ["action", "busy", "now", "onApprove", "onReject"])
                ]))
          ], 64))
        }), 128))
      ]))
    : _createCommentVNode("", true)
}
}

})